import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import provideHook from "@/lib/provideHook";

import type { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";

const statusOrder: AppointmentStatus[] = [
  "pending",
  "accepted",
  "running",
  "completed",
  "cancelled",
  "lost_opportunity"
];
type TimeField =
  | "accepted_time"
  | "dispatch_time"
  | "arrival_time"
  | "departure_time";

type AppointmentTimes = Partial<Record<TimeField, string>>;

type StatusRequiredTimes = Record<AppointmentStatus, TimeField[]>;
type StatusTimeLosses = Record<
  AppointmentStatus,
  Partial<Record<AppointmentStatus, TimeField[]>>
>;

const APPOINTMENT_STATUS_REQUIRED_TIMES: StatusRequiredTimes = {
  pending: [],
  accepted: ["accepted_time", "dispatch_time"],
  running: ["accepted_time", "dispatch_time", "arrival_time"],
  completed: [
    "accepted_time",
    "dispatch_time",
    "arrival_time",
    "departure_time"
  ],
  cancelled: [
    "accepted_time",
    "dispatch_time",
    "arrival_time",
    "departure_time"
  ],
  lost_opportunity: [] // Adjust if needed
};

const APPOINTMENT_STATUS_TIME_LOSSES: StatusTimeLosses = {
  pending: {},
  accepted: {
    pending: ["accepted_time", "dispatch_time"]
  },
  running: {
    pending: ["accepted_time", "dispatch_time", "arrival_time"],
    accepted: ["arrival_time"]
  },
  completed: {
    pending: [
      "accepted_time",
      "dispatch_time",
      "arrival_time",
      "departure_time"
    ],
    accepted: ["arrival_time", "departure_time"],
    running: ["departure_time"]
  },
  cancelled: {
    pending: [
      "accepted_time",
      "dispatch_time",
      "arrival_time",
      "departure_time"
    ],
    accepted: ["arrival_time", "departure_time"],
    running: ["departure_time"]
  },
  lost_opportunity: {}
};

interface ChangeValidationResult {
  success: boolean;
  missing?: TimeField;
}
export const useAppointmentStatus = provideHook(
  (appointment: AppointmentV2) => {
    const store = useStore();

    const status = ref<AppointmentStatus>(appointment.status);
    const displayStatus = computed(() => appointment.status);

    const times = reactive<AppointmentTimes>({
      accepted_time: appointment.accepted_time || "",
      dispatch_time: appointment.dispatch_time || "",
      arrival_time: appointment.arrival_time || "",
      departure_time: appointment.departure_time || ""
    });

    function getRequiredTimesFor(newStatus: AppointmentStatus): TimeField[] {
      return APPOINTMENT_STATUS_REQUIRED_TIMES[newStatus] || [];
    }

    function getRequiredFieldsForStatus(
      toStatus: AppointmentStatus
    ): TimeField[] {
      return APPOINTMENT_STATUS_REQUIRED_TIMES[toStatus] || [];
    }

    function getTimesToRemove(
      fromStatus: AppointmentStatus,
      toStatus: AppointmentStatus
    ): TimeField[] {
      if (!isBackwardTransition(fromStatus, toStatus)) return [];
      return APPOINTMENT_STATUS_TIME_LOSSES[fromStatus]?.[toStatus] || [];
    }

    function canChangeStatus(
      toStatus: AppointmentStatus,
      newTimes: AppointmentTimes
    ): ChangeValidationResult {
      const required = getRequiredTimesFor(toStatus);
      for (const key of required) {
        if (!newTimes[key]) {
          return {
            success: false,
            missing: key
          };
        }
      }
      return { success: true };
    }

    function isBackwardTransition(
      from: AppointmentStatus,
      to: AppointmentStatus
    ): boolean {
      return statusOrder.indexOf(to) < statusOrder.indexOf(from);
    }

    function applyTimeChanges(
      newTimes: AppointmentTimes,
      toStatus: AppointmentStatus
    ) {
      for (const [key, value] of Object.entries(newTimes)) {
        (times as any)[key] = value;
      }
      for (const key of getTimesToRemove(status.value, toStatus)) {
        delete times[key as TimeField];
      }
    }

    // Key by key rather than by reassignment: the caller's modal binds to this
    // same reactive object, so replacing it would strand those bindings.
    function restoreTimes(snapshot: AppointmentTimes) {
      for (const key of Object.keys(times) as TimeField[]) {
        if (!(key in snapshot)) delete times[key];
      }
      Object.assign(times, snapshot);
    }

    // overrideReason is optional and trailing so the five other transitions keep
    // calling this unchanged.
    async function changeStatus(
      toStatus: AppointmentStatus,
      newTimes: AppointmentTimes,
      reason: string,
      link: { href: string; method: string },
      overrideReason?: string
    ): Promise<void> {
      // Both are mutated before the request, so a rejection has to undo them.
      // Left diverged, the next attempt computes getTimesToRemove from a status
      // the appointment never reached and drops a time the caller's warning
      // promised to keep.
      const previousTimes: AppointmentTimes = { ...times };
      const previousStatus = status.value;

      applyTimeChanges(newTimes, toStatus);
      status.value = toStatus;

      try {
        await store.dispatch("updateAppointmentStatusAndTimes", {
          link,
          params: {
            ...times,
            reason,
            // Spread conditionally, never `custom_form_override_reason:
            // overrideReason`. The server treats the key's presence as the
            // authority to waive a required-form gate, so sending it as
            // undefined on an ordinary save would let any completion with a note
            // bypass the gate before the admin has seen what they waived.
            ...(overrideReason
              ? { custom_form_override_reason: overrideReason }
              : {})
          }
        });
      } catch (error) {
        restoreTimes(previousTimes);
        status.value = previousStatus;
        throw error;
      }
    }

    return {
      status,
      displayStatus,
      times,
      canChangeStatus,
      changeStatus,
      getRequiredFieldsForStatus,
      getTimesToRemove
    };
  }
);
